"use client";

import { useEffect, useRef } from "react";
import {
  useMicController,
  useMicPermission,
  useMicSensitivity,
  useMicToggle,
} from "@/contexts/studio-context";
import { Group, Line, PermBadge, SectionHeader, Slider, StubPicker, Switch } from "./atoms";
import { SET } from "./palette";

export function MicSection() {
  const permission = useMicPermission();
  const [sensitivity, setSensitivity] = useMicSensitivity();
  const { state, pending, toggle, error } = useMicToggle();

  const micActive = state === "on";

  // WebKit ne connaît pas le nom `microphone` dans l'API Permissions, la sonde
  // y répond donc "indisponible" alors que la capture, elle, marche. Tant que
  // le micro n'est pas réellement hors de portée, on présente la pastille comme
  // une invitation à autoriser : le clic déclenche `getUserMedia`, qui est le
  // seul juge de la permission sur cette plateforme.
  const badgePermission =
    permission === "unsupported" && state !== "unavailable" ? "prompt" : permission;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        kicker="Détection par micro"
        trailing={
          <>
            <PermBadge
              permission={badgePermission}
              onRequest={!micActive ? () => void toggle() : undefined}
            />
            <Switch
              checked={micActive}
              disabled={pending}
              onChange={() => void toggle()}
              label="Activer la détection par micro"
            />
          </>
        }
      />

      {error ? (
        <p
          role="alert"
          style={{
            margin: 0,
            padding: "8px 12px",
            borderRadius: 8,
            border: `1px solid ${SET.border}`,
            background: "rgba(248, 113, 113, 0.08)",
            color: SET.red,
            fontSize: 12,
            lineHeight: 1.45,
          }}
        >
          {error}
        </p>
      ) : null}

      <Group>
        <Line
          withDivider={false}
          label="Source audio"
          hint="Périphérique d'entrée utilisé"
          control={<StubPicker label="Micro intégré (par défaut)" />}
        />
        <Line
          label="Niveau d'entrée"
          hint={micActive ? "Signal en direct" : "Active le micro pour voir le niveau"}
          control={<VuMeter active={micActive} />}
        />
        <Line
          label="Sensibilité"
          hint="Seuil de détection, plus haut pour les notes douces"
          control={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Slider
                value={sensitivity}
                onChange={setSensitivity}
                disabled={!micActive}
              />
              <span
                style={{
                  fontSize: 11,
                  color: SET.textMute,
                  minWidth: 28,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {sensitivity}
              </span>
            </div>
          }
        />
      </Group>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// VuMeter, canvas-based level indicator. Reads RMS amplitude off
// the live MicrophoneInput's AnalyserNode and renders 14 vertical
// bars, violet → amber from bottom to top, opacity proxy for level.
// One canvas + one rAF loop avoids the React-reconciliation pitfalls
// of writing inline styles on 14 individual divs.
// ──────────────────────────────────────────────────────────────

const SEGMENTS = 14;
const METER_WIDTH = 140;
const METER_HEIGHT = 16;
const SEGMENT_GAP = 2;

function VuMeter({ active }: { active: boolean }) {
  const mic = useMicController();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Re-paint when the canvas is unmounted/empty (initial render) or when the
  // mic toggles off, so the dim baseline shows up immediately.
  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return;
    }

    // Match canvas backing store to its CSS box for crisp rendering on HiDPI.
    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    canvas.width = Math.round(METER_WIDTH * dpr);
    canvas.height = Math.round(METER_HEIGHT * dpr);
    ctx.scale(dpr, dpr);

    const draw = (lit: number) => {
      ctx.clearRect(0, 0, METER_WIDTH, METER_HEIGHT);
      const segWidth = (METER_WIDTH - SEGMENT_GAP * (SEGMENTS - 1)) / SEGMENTS;

      for (let i = 0; i < SEGMENTS; i++) {
        const x = i * (segWidth + SEGMENT_GAP);
        const segHeight = 4 + (i / SEGMENTS) * 12;
        const y = METER_HEIGHT - segHeight;
        const isLit = i < lit;

        ctx.globalAlpha = isLit ? 1 : 0.15;
        ctx.fillStyle = !isLit
          ? SET.borderHi
          : i > SEGMENTS - 3
            ? SET.amber
            : SET.violet;
        ctx.fillRect(x, y, segWidth, segHeight);
      }

      ctx.globalAlpha = 1;
    };

    if (!active || !mic) {
      draw(0);

      return;
    }

    const input = mic.getInput();
    const analyser = input?.getAnalyser();

    if (!analyser) {
      draw(0);

      return;
    }

    const buffer = new Float32Array(analyser.fftSize);
    let raf = 0;
    // Smooth the bar count over time so we don't flicker between adjacent
    // values frame-to-frame (the meter feels live without strobing).
    let smooth = 0;

    const tick = () => {
      analyser.getFloatTimeDomainData(buffer as Float32Array<ArrayBuffer>);

      let sum = 0;

      for (let i = 0; i < buffer.length; i++) {
        sum += buffer[i] * buffer[i];
      }

      // Map RMS (0..~0.5 in practice) → 0..1, with mild log scaling so
      // quiet input still lights up the lower bars.
      const rms = Math.sqrt(sum / buffer.length);
      const norm = Math.min(1, Math.sqrt(rms * 10));
      const target = norm * SEGMENTS;
      // Asymmetric smoothing, fast attack, slower release feels natural.
      const factor = target > smooth ? 0.5 : 0.18;
      smooth += (target - smooth) * factor;
      const lit = Math.round(smooth);

      draw(lit);
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [active, mic]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      style={{
        width: METER_WIDTH,
        height: METER_HEIGHT,
        display: "block",
      }}
    />
  );
}
